import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from './api';
import LedgerTab from './components/LedgerTab';
import { useMultiHOA } from './MultiHOAContext';
import StripeExpressSetup from './components/StripeExpressSetup';
import AutopaySettings from './components/AutopaySettings';

const PaymentSystem = ({ user, isBoard = false }) => {
  const { activeHoaId } = useMultiHOA();
  const [activeView, setActiveView] = useState('overview');
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [configMode, setConfigMode] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [showBoardPayment, setShowBoardPayment] = useState(false);
  
  // Payment method preference (bank or card) - persistent per user
  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState('bank'); // Default to bank (lower fee)
  
  // Board Member Personal Payment State
  const [boardMemberBalance, setBoardMemberBalance] = useState(250.00);
  const [boardPaymentMethod, setBoardPaymentMethod] = useState('credit_card');
  const [boardAutoPayEnabled, setBoardAutoPayEnabled] = useState(false);
  
  const [selectedResident, setSelectedResident] = useState(null);
  const [manualPaymentData, setManualPaymentData] = useState({
    amount: '',
    paymentMethod: 'check',
    checkNumber: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Enhanced Payment Management with Access Control and Filters
  const [paymentFilters, setPaymentFilters] = useState({
    status: 'all', // all, paid, unpaid, overdue
    dateRange: 'all', // all, this_month, last_month, this_year
    resident: 'all'
  });
  
  const [accessSettings, setAccessSettings] = useState({
    viewPermissions: 'board_only', // board_only, treasurer_only, all_homeowners
    editPermissions: 'treasurer_only',
    reminderPermissions: 'board_only'
  });

  // REAL residents data from ledger - dynamically loaded
  const [residents, setResidents] = useState([]);

  // REAL Board Member Stripe Payment Processing
  const processBoardMemberPayment = async () => {
    try {
      setLoading(true);
      
      // Create real Stripe checkout session for board member
      const paymentData = {
        payment_type: 'board_member_dues',
        amount: boardMemberBalance,
        customer_email: user?.email || 'treasurer@testpayment.com',
        description: 'Board Member HOA Dues Payment'
      };
      
      const response = await axios.post('/api/payment/create-checkout-session', paymentData);
      
      if (response.data.success) {
        // Redirect to REAL Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
      
    } catch (error) {
      setLoading(false);
      console.error('Board member payment error:', error);
      alert('❌ Payment setup failed: ' + (error.response?.data?.detail || error.message) + '\n\nPlease contact support or try again later.');
    }
  };

  // REAL manual payment processing - syncs with Ledger
  const processManualPayment = async () => {
    if (!selectedResident || !manualPaymentData.amount) {
      alert('❌ Please select a resident and enter payment amount');
      return;
    }
    
    const amount = parseFloat(manualPaymentData.amount);
    
    try {
      setLoading(true);
      
      // Record payment in ledger - this will mark unpaid entries as paid
      const currentHoaId = activeHoaId || user?.hoa_id;
      const paymentPayload = {
        property_address: selectedResident.address || selectedResident.name,
        amount: amount,
        payment_method: manualPaymentData.paymentMethod,
        payment_date: manualPaymentData.paymentDate,
        payment_id: `manual_${Date.now()}`,
        notes: manualPaymentData.notes
      };
      
      const response = await api.post(`/api/ledger/${currentHoaId}/record-payment`, paymentPayload);
      
      if (response.data.success) {
        // Update local state
        setResidents(prev => prev.map(r => 
          r.id === selectedResident.id 
            ? { ...r, balance: Math.max(0, r.balance - amount), status: (r.balance - amount) <= 0 ? 'paid' : 'overdue', lastPayment: manualPaymentData.paymentDate }
            : r
        ));
        
        // Add transaction record for display
        const newTransaction = {
          id: paymentPayload.payment_id,
          amount: amount,
          description: `Manual Payment - ${manualPaymentData.paymentMethod.replace('_', ' ')}${manualPaymentData.checkNumber ? ` #${manualPaymentData.checkNumber}` : ''}`,
          payment_status: 'paid',
          created_at: manualPaymentData.paymentDate,
          payment_method: manualPaymentData.paymentMethod,
          user_name: selectedResident.name,
          user_email: selectedResident.email,
          notes: manualPaymentData.notes
        };
        
        setTransactions(prev => [newTransaction, ...prev]);
        
        // Send confirmation email
        try {
          await api.post('/api/emails/send-payment-confirmation', {
            recipient_email: selectedResident.email,
            resident_name: selectedResident.name,
            amount: amount.toFixed(2),
            payment_method: manualPaymentData.paymentMethod,
            payment_date: manualPaymentData.paymentDate,
            balance_remaining: Math.max(0, selectedResident.balance - amount)
          });
        } catch (emailError) {
          console.error('Email confirmation failed:', emailError);
        }
        
        alert(`✅ MANUAL PAYMENT RECORDED!\n\n👤 Resident: ${selectedResident.name}\n💰 Amount: $${amount.toFixed(2)}\n💳 Method: ${manualPaymentData.paymentMethod.replace('_', ' ')}\n📅 Date: ${new Date(manualPaymentData.paymentDate).toLocaleDateString()}\n💰 New Balance: $${Math.max(0, selectedResident.balance - amount).toFixed(2)}\n\n✅ Ledger updated\n📧 Confirmation email sent\n📊 Transaction recorded`);
        
        // Reset form
        setManualPaymentData({
          amount: '',
          paymentMethod: 'check',
          checkNumber: '',
          paymentDate: new Date().toISOString().split('T')[0],
          notes: ''
        });
        setSelectedResident(null);
        setShowManualPayment(false);
        
        // Refresh payment data to reflect ledger changes
        await fetchPaymentData();
      } else {
        throw new Error(response.data.message || 'Failed to record payment');
      }
      
    } catch (error) {
      console.error('Manual payment error:', error);
      alert('❌ Failed to process manual payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // REAL CSV export function
  const exportPaymentReport = () => {
    const csvData = residents.map(r => [
      r.name,
      r.email,
      r.address,
      `$${r.balance.toFixed(2)}`,
      r.status.toUpperCase(),
      r.lastPayment ? new Date(r.lastPayment).toLocaleDateString() : 'Never',
      r.balance > 0 ? 'OVERDUE' : 'CURRENT'
    ]);
    
    const headers = ['Name', 'Email', 'Address', 'Balance', 'Status', 'Last Payment', 'Account Status'];
    const csvContent = [headers, ...csvData].map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `HOA_Payment_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert(`✅ PAYMENT REPORT EXPORTED!\n\n📊 File: HOA_Payment_Report_${new Date().toISOString().split('T')[0]}.csv\n📁 Contains: ${residents.length} residents\n💰 Total Outstanding: $${residents.reduce((sum, r) => sum + r.balance, 0).toFixed(2)}\n\n📥 File downloaded to your Downloads folder`);
  };

  // Enhanced filtering function
  const applyFilters = (residentsList) => {
    let filtered = [...residentsList];
    
    // Filter by status
    if (paymentFilters.status !== 'all') {
      filtered = filtered.filter(r => {
        if (paymentFilters.status === 'paid') return r.balance === 0;
        if (paymentFilters.status === 'unpaid') return r.balance > 0;
        if (paymentFilters.status === 'overdue') {
          const lastPaymentDate = new Date(r.lastPayment);
          const daysSincePayment = (new Date() - lastPaymentDate) / (1000 * 60 * 60 * 24);
          return r.balance > 0 && daysSincePayment > 30;
        }
        return true;
      });
    }
    
    // Filter by date range
    if (paymentFilters.dateRange !== 'all') {
      const now = new Date();
      filtered = filtered.filter(r => {
        const lastPayment = new Date(r.lastPayment);
        if (paymentFilters.dateRange === 'this_month') {
          return lastPayment.getMonth() === now.getMonth() && lastPayment.getFullYear() === now.getFullYear();
        }
        if (paymentFilters.dateRange === 'last_month') {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          return lastPayment.getMonth() === lastMonth.getMonth() && lastPayment.getFullYear() === lastMonth.getFullYear();
        }
        if (paymentFilters.dateRange === 'this_year') {
          return lastPayment.getFullYear() === now.getFullYear();
        }
        return true;
      });
    }
    
    return filtered;
  };

  // Enhanced reminder system with targeting
  const sendTargetedReminders = async (targetType = 'overdue', customMessage = '') => {
    let targetResidents = [];
    
    switch (targetType) {
      case 'overdue':
        targetResidents = residents.filter(r => {
          const daysSincePayment = (new Date() - new Date(r.lastPayment)) / (1000 * 60 * 60 * 24);
          return r.balance > 0 && daysSincePayment > 30;
        });
        break;
      case 'upcoming':
        // Residents with payments due in next 7 days
        targetResidents = residents.filter(r => r.balance > 0);
        break;
      case 'all_unpaid':
        targetResidents = residents.filter(r => r.balance > 0);
        break;
      default:
        targetResidents = residents.filter(r => r.balance > 0);
    }
    
    if (targetResidents.length === 0) {
      alert(`✅ No residents found for ${targetType} reminders!`);
      return;
    }
    
    // Check access permissions
    if (accessSettings.reminderPermissions === 'treasurer_only' && user.role !== 'treasurer') {
      alert('❌ Only treasurers can send payment reminders');
      return;
    }
    
    if (accessSettings.reminderPermissions === 'board_only' && !['president', 'vice_president', 'treasurer', 'secretary', 'board_member'].includes(user.role)) {
      alert('❌ Only board members can send payment reminders');  
      return;
    }
    
    setLoading(true);
    let successCount = 0;
    
    try {
      for (const resident of targetResidents) {
        try {
          await axios.post('/api/emails/send-payment-reminder', {
            recipient_email: resident.email,
            resident_name: resident.name,
            hoa_name: 'Your HOA Community',
            period: 'monthly',
            amount: resident.balance.toFixed(2),
            due_date: 'February 1st, 2025',
            late_fee: '25.00',
            custom_message: customMessage,
            reminder_type: targetType
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to send reminder to ${resident.email}:`, error);
        }
      }
      
      alert(`✅ ${targetType.toUpperCase()} REMINDERS SENT!\n\n📧 Emails sent: ${successCount}/${targetResidents.length}\n💰 Total reminded: $${targetResidents.reduce((sum, r) => sum + r.balance, 0).toFixed(2)}\n📅 Reminder date: ${new Date().toLocaleDateString()}\n\n✅ All targeted residents have been notified`);
      
    } catch (error) {
      alert(`⚠️ REMINDERS SENT WITH SOME ISSUES\n\n📧 Successfully sent: ${successCount}\n❌ Failed: ${targetResidents.length - successCount}\n\n📋 All available reminders have been sent`);
    }
    
    setLoading(false);
  };

  // REAL send overdue reminders (enhanced version)
  const sendOverdueReminders = async () => {
    await sendTargetedReminders('overdue');
  };

  // Record payment and create budget transaction
  const recordPayment = async (paymentData) => {
    try {
      setLoading(true);
      const response = await api.post('/api/payments/record', paymentData);
      
      if (response.data.success) {
        alert(`✅ PAYMENT RECORDED SUCCESSFULLY!\n\n💰 Amount: $${paymentData.amount}\n📧 Method: ${paymentData.payment_method}\n🏦 Transaction created in Budget system\n📋 Payment ID: ${response.data.payment_id}\n\n✅ Payment has been processed and budget updated`);
        
        // Refresh data if needed
        console.log('Payment recorded, refreshing data...');
        return true;
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('❌ PAYMENT RECORDING FAILED\n\n' + (error.response?.data?.detail || error.message));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Record manual payment (board members only)
  const recordManualPayment = async (paymentData) => {
    try {
      setLoading(true);
      const response = await api.post('/api/payments/manual', paymentData);
      
      if (response.data.success) {
        alert(`✅ MANUAL PAYMENT RECORDED!\n\n💰 Amount: $${paymentData.amount}\n📧 Method: ${paymentData.payment_method}\n👤 Resident: ${paymentData.resident_name || 'Current User'}\n🏦 Transaction created in Budget system\n📋 Payment ID: ${response.data.payment_id}\n\n✅ Manual payment processed successfully`);
        
        // Refresh payment data
        console.log('Manual payment recorded, refreshing data...');
        return true;
      }
    } catch (error) {
      console.error('Error recording manual payment:', error);
      alert('❌ MANUAL PAYMENT FAILED\n\n' + (error.response?.data?.detail || error.message));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Configuration state
  const [tempConfig, setTempConfig] = useState({
    payment_methods_enabled: ['online'],
    dues_amount: 250.0,
    dues_frequency: 'monthly', // monthly, quarterly, yearly
    due_day: 1,
    due_month: 1, // For yearly payments: 1-12
    automatic_dues_enabled: true, // Auto-create dues based on frequency
    late_fee_amount: 0.0,
    late_fee_days: 30,
    mail_payment_address: '',
    mail_payment_instructions: '',
    online_payment_enabled: true,
    check_payment_enabled: false,
    money_order_enabled: false,
    external_payment_enabled: false,
    auto_reminders_enabled: true,
    autopay_enabled: false,
    autopay_discount_percent: 0.0,
    reminder_days_before: [7, 3, 1],
    reminder_days_after: [5, 15, 30]
  });

  useEffect(() => {
    fetchPaymentData();
    checkForStripeReturn();
    loadAllUsers();
  }, []);

  // Update board member balance when config changes
  useEffect(() => {
    if (paymentConfig?.dues_amount) {
      setBoardMemberBalance(paymentConfig.dues_amount);
    }
  }, [paymentConfig]);

  // Check if user is returning from Stripe payment
  const checkForStripeReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setActiveView('overview');
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Poll payment status after Stripe redirect
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please refresh the page to see updated status.');
      return;
    }

    try {
      const response = await axios.get(`/api/payment/checkout-status/${sessionId}`);
      
      if (response.data.success) {
        const { payment_status, status } = response.data;
        
        if (payment_status === 'paid') {
          alert('Payment successful! Thank you for your payment.');
          await fetchPaymentData(); // Refresh payment data
          return;
        } else if (status === 'expired') {
          setError('Payment session expired. Please try again.');
          return;
        }

        // If payment is still pending, continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Error checking payment status. Please refresh the page.');
    }
  };

  const handleGenerateDues = async () => {
    try {
      setLoading(true);
      const year = new Date().getFullYear();
      const response = await axios.post(`/api/ledger/${user.hoa_id}/sync-dues`, { year });
      alert(`✅ ${response.data.message}\n\nDues have been generated based on your payment configuration. The system will now automatically generate dues based on your frequency (monthly/quarterly/yearly) until you change the amount in Configuration.`);
      // Refresh data
      await fetchPaymentData();
    } catch (error) {
      console.error('Error generating dues:', error);
      alert('❌ Failed to generate dues: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Make it available globally for the button
  React.useEffect(() => {
    window.onGenerateDues = handleGenerateDues;
    return () => {
      delete window.onGenerateDues;
    };
  }, [user]);

  const fetchPaymentData = async () => {
    setLoading(true);
    setError(''); // Clear previous errors
    try {
      // Fetch payment configuration
      const configResponse = await axios.get('/api/payment/configuration');
      if (configResponse.data.success) {
        setPaymentConfig(configResponse.data.configuration);
        setTempConfig(configResponse.data.configuration);
      }

      // Fetch payment summary (board members only)
      if (isBoard) {
        try {
          console.log('[Overview] Fetching payment summary for board member...');
          const summaryResponse = await axios.get('/api/payment/summary');
          console.log('[Overview] Payment summary response:', summaryResponse.data);
          if (summaryResponse.data.success) {
            setPaymentSummary(summaryResponse.data.summary);
            console.log('[Overview] Summary set:', summaryResponse.data.summary);
          }
        } catch (summaryError) {
          console.warn('[Overview] Could not fetch payment summary:', summaryError);
        }
        
        // Fetch all ledgers to populate residents data
        try {
          const ledgersResponse = await axios.get(`/api/ledger/${user.hoa_id}/all-ledgers`);
          if (ledgersResponse.data && ledgersResponse.data.ledgers) {
            const ledgers = ledgersResponse.data.ledgers;
            // Convert ledger data to residents format
            const residentsData = ledgers.map((ledger, index) => ({
              id: index + 1,
              name: ledger.account_holder_names?.[0] || 'Unknown',
              email: ledger.emails?.[0] || '',
              address: ledger.property_address,
              balance: ledger.balance_due || 0,
              status: (ledger.balance_due || 0) > 0 ? 'overdue' : 'paid',
              lastPayment: ledger.last_payment_date || null
            }));
            setResidents(residentsData);
          }
        } catch (ledgerError) {
          console.warn('Could not fetch ledger data for residents:', ledgerError);
          setResidents([]);
        }
      }

      // Fetch transactions - board members see ALL, homeowners see their own
      // Include status_filter=all to get both paid and unpaid transactions for history
      const transactionsEndpoint = isBoard 
        ? '/api/payment/all-transactions?status_filter=all' 
        : '/api/payment/my-transactions';
      try {
        const transactionsResponse = await axios.get(transactionsEndpoint);
        if (transactionsResponse.data && transactionsResponse.data.transactions) {
          setTransactions(transactionsResponse.data.transactions);
        }
      } catch (transError) {
        console.warn('Could not fetch transactions:', transError);
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
      setError('Failed to load payment information. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayOnline = async (amount = null) => {
    try {
      setLoading(true);
      
      // Use Stripe Express payment system
      const items = [{
        description: `HOA Dues Payment`,
        amount: amount || paymentConfig?.dues_amount || 0
      }];

      // Show payment method choice modal instead of going straight to checkout
      setPendingPaymentItems(items);
      setShowPaymentMethodChoice(true);
      setLoading(false);
      return;
    } catch (error) {
      console.error('Payment error:', error);
      
      // Check if Stripe Express is not set up
      if (error.response?.status === 404 || error.response?.data?.detail?.includes('Express account not found')) {
        setError('HOA has not set up payment processing yet. Please contact your board.');
      } else {
        setError(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    try {


  const createPaymentWithMethod = async (paymentMethod) => {
    try {
      setLoading(true);
      setShowPaymentMethodChoice(false);
      
      const response = await api.post('/api/stripe/express/payment/create-session', {
        items: pendingPaymentItems,
        payment_method: paymentMethod  // 'card' or 'bank'
      });
      
      if (response.data.success) {
        // Redirect to Stripe checkout
        window.location.href = response.data.session_url;
      } else {
        throw new Error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  // Calculate processing fee using gross-up formula
  // Formula: fee = (rate * amount + fixed) / (1 - rate)
  // This ensures Stripe's fee on the total is fully covered
  const calculateFee = (amount, method) => {
    if (method === 'bank') {
      const rate = 0.008;
      const fixed = 0.00;
      const fee = (rate * amount + fixed) / (1 - rate);
      return Math.min(fee, 5.00); // Cap at $5
    } else {
      // Card: 2.9% + $0.30
      const rate = 0.029;
      const fixed = 0.30;
      return (rate * amount + fixed) / (1 - rate);
    }
  };

  const calculateTotal = (amount, method) => {
    return amount + calculateFee(amount, method);
  };

      setLoading(true);
      const response = await axios.post('/api/board/payment/configuration', tempConfig);
      
      if (response.data.success) {
        setPaymentConfig(tempConfig);
        setConfigMode(false);
        await fetchPaymentData(); // Refresh data
      }
    } catch (error) {
      console.error('Config save error:', error);
      setError('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const createMonthlyDues = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/board/payment/create-monthly-dues');
      
      if (response.data.success) {
        alert(`Created ${response.data.created_count} payment transactions`);
        await fetchPaymentData(); // Refresh data
      }
    } catch (error) {
      console.error('Create dues error:', error);
      setError('Failed to create monthly dues');
    } finally {
      setLoading(false);
    }
  };

  // New function to create special fees/assessments
  const createSpecialFee = async (feeData) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/board/payment/create-special-fee', feeData);
      
      if (response.data.success) {
        alert(`Created special fee for ${response.data.created_count} member(s)`);
        await fetchPaymentData(); // Refresh data
        setShowCreateFeeModal(false);
      }
    } catch (error) {
      console.error('Create fee error:', error);
      setError('Failed to create special fee');
    } finally {
      setLoading(false);
    }
  };

  // State for create fee modal
  const [showCreateFeeModal, setShowCreateFeeModal] = useState(false);
  const [feeForm, setFeeForm] = useState({
    title: '',
    description: '',
    amount: '',
    due_date: '',
    fee_type: 'assessment', // assessment, fine, late_fee, other
    apply_to: 'all', // all, selected
    selected_members: []
  });

  // State for create payment modal
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [newPayment, setNewPayment] = useState({
    user_id: '',
    payment_type: 'dues',
    amount: '',
    due_date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [uploading, setUploading] = useState(false);

  const sendReminders = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/board/payment/send-reminders', {
        reminder_type: 'overdue'
      });
      
      if (response.data.success) {
        alert(`Reminders sent to ${response.data.sent_count} homeowners`);
      }
    } catch (error) {
      console.error('Send reminders error:', error);
      setError('Failed to send reminders');
    } finally {
      setLoading(false);
    }
  };

  // Load all users for payment creation
  const loadAllUsers = async () => {
    try {
      const response = await api.get('/api/users/all');
      if (response.data.success) {
        setAllUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleCreatePayment = async () => {
    try {
      if (!newPayment.user_id || !newPayment.amount || !newPayment.due_date) {
        alert('Please fill in all required fields');
        return;
      }

      setUploading(true);
      
      const paymentData = {
        user_id: newPayment.user_id,
        payment_type: newPayment.payment_type,
        amount: parseFloat(newPayment.amount),
        due_date: newPayment.due_date,
        description: newPayment.description || `${newPayment.payment_type} payment`,
        payment_status: 'pending'
      };

      const response = await api.post('/api/payments/create', paymentData);
      
      if (response.data.success) {
        alert(`✅ PAYMENT CREATED SUCCESSFULLY!\n\n💰 Amount: $${paymentData.amount.toFixed(2)}\n👤 User: ${allUsers.find(u => u.id === paymentData.user_id)?.first_name} ${allUsers.find(u => u.id === paymentData.user_id)?.last_name}\n📅 Due Date: ${new Date(paymentData.due_date).toLocaleDateString()}\n📋 Type: ${paymentData.payment_type}\n\n✅ Payment has been created and user will be notified`);
        
        // Reset form
        setNewPayment({
          user_id: '',
          payment_type: 'dues',
          amount: '',
          due_date: new Date().toISOString().split('T')[0],
          description: ''
        });
        setShowCreatePayment(false);
        
        // Refresh data
        await fetchPaymentData();
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('❌ PAYMENT CREATION FAILED\n\n' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-800 bg-green-100';
      case 'pending': return 'text-yellow-800 bg-yellow-100';
      case 'failed': return 'text-red-800 bg-red-100';
      case 'expired': return 'text-gray-800 bg-gray-100';
      default: return 'text-gray-800 bg-gray-100';
    }
  };

  if (loading && !paymentConfig) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
          </svg>
          <p className="mt-2 text-sm text-red-600">{error}</p>
          <button
            onClick={() => { setError(''); fetchPaymentData(); }}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              💳 Payment Center
            </h1>
            <p className="text-gray-600 text-base">
              {isBoard ? 'Manage HOA payments and dues collection' : 'View and pay your HOA dues'}
            </p>
          </div>
          {isBoard && (
            <div className="flex space-x-2">
              <button
                data-testid="sync-stripe-btn"
                onClick={async () => {
                  try {
                    setSyncing(true);
                    const { data } = await api.post(`/api/stripe/express/reconcile/${hoaId}`);
                    if (data.recovered > 0) {
                      alert(`Recovered ${data.recovered} payment(s) from Stripe!\n\n${data.recovered_details?.map(d => `• $${d.amount.toFixed(2)} (${d.user_email})`).join('\n') || ''}\n\nRefreshing...`);
                      await fetchPaymentData();
                    } else if (data.expired > 0) {
                      alert(`Sync complete. ${data.expired} expired session(s) cleaned up. No missing payments found.`);
                    } else {
                      alert('All payments are in sync with Stripe.');
                    }
                    if (data.not_in_db > 0) {
                      alert(`Note: ${data.not_in_db} payment(s) found in Stripe but missing from the database. These may need manual review.`);
                    }
                  } catch (err) {
                    console.error('Reconciliation error:', err);
                    alert('Failed to sync with Stripe. Please try again.');
                  } finally {
                    setSyncing(false);
                  }
                }}
                disabled={syncing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {syncing ? 'Syncing...' : 'Sync with Stripe'}
              </button>
              <button
                onClick={() => setConfigMode(!configMode)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {configMode ? 'Cancel' : 'Configure'}
              </button>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 md:space-x-8 overflow-x-auto scrollbar-hide pb-2 md:pb-0">
            <button
              onClick={() => setActiveView('overview')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'overview'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            {isBoard && (
              <>
                <button
                  onClick={() => setActiveView('payment-setup')}
                  className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                    activeView === 'payment-setup'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  🔧 Payment Setup
                </button>
                <button
                  onClick={() => setActiveView('management')}
                  className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                    activeView === 'management'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Payment Management
                </button>
                <button
                  onClick={() => setActiveView('manual')}
                  className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                    activeView === 'manual'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Manual Payments
                </button>
              </>
            )}
            <button
              onClick={() => setActiveView('ledger')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'ledger'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📒 Ledger
            </button>
            {/* AUTOPAY TAB - TEMPORARILY HIDDEN - NOT READY FOR PRODUCTION
            <button
              onClick={() => setActiveView('autopay-setup')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'autopay-setup'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💳 Autopay
            </button>
            */}
            <button
              onClick={() => setActiveView('history')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeView === 'history'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Payment History
            </button>
          </nav>
        </div>
      </div>

      {/* Configuration Panel */}
      {configMode && isBoard && (
        <PaymentConfigurationPanel
          config={tempConfig}
          setConfig={setTempConfig}
          onSave={saveConfiguration}
          onCancel={() => setConfigMode(false)}
          loading={loading}
        />
      )}

      {/* Content Views */}
      {activeView === 'payment-setup' && isBoard && (
        <div className="p-6">
          <StripeExpressSetup hoaId={activeHoaId || user?.hoa_id} />
        </div>
      )}

      {activeView === 'overview' && (
        <PaymentOverview
          config={paymentConfig}
          summary={paymentSummary}
          transactions={transactions}
          isBoard={isBoard}
          user={user}
          onPayOnline={handlePayOnline}
          setActiveView={setActiveView}
          preferredPaymentMethod={preferredPaymentMethod}
          setPreferredPaymentMethod={setPreferredPaymentMethod}
        />
      )}

      {activeView === 'management' && isBoard && (
        <PaymentManagement
          summary={paymentSummary}
          transactions={transactions}
          onCreateSpecialFee={() => setShowCreateFeeModal(true)}
          onCreatePayment={() => setShowCreatePayment(true)}
          onSendReminders={sendOverdueReminders}
          onShowManualPayment={() => setShowManualPayment(true)}
          onExportReport={exportPaymentReport}
          overdueCount={residents.filter(r => r.balance > 0).length}
          loading={loading}
          paymentFilters={paymentFilters}
          setPaymentFilters={setPaymentFilters}
          residents={residents}
          applyFilters={applyFilters}
          sendTargetedReminders={sendTargetedReminders}
        />
      )}

      {activeView === 'manual' && isBoard && (
        <ManualPaymentManager
          transactions={transactions}
          onRefresh={fetchPaymentData}
        />
      )}

      {activeView === 'history' && (
        <PaymentHistory
          transactions={transactions}
          isBoard={isBoard}
          user={user}
        />
      )}

      {activeView === 'ledger' && (
        <LedgerTab />
      )}

      {/* AUTOPAY SETTINGS - TEMPORARILY HIDDEN - NOT READY FOR PRODUCTION
      {activeView === 'autopay-setup' && (
        <AutopaySettings
          user={user}
          isBoard={isBoard}
        />
      )}
      */}
      
      {/* Create Special Fee Modal */}
      {showCreateFeeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create Special Fee</h3>
              <button
                onClick={() => setShowCreateFeeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); createSpecialFee(feeForm); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fee Title</label>
                  <input
                    type="text"
                    required
                    value={feeForm.title}
                    onChange={(e) => setFeeForm({...feeForm, title: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Pool Maintenance Assessment"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fee Type</label>
                  <select
                    value={feeForm.fee_type}
                    onChange={(e) => setFeeForm({...feeForm, fee_type: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="assessment">Special Assessment</option>
                    <option value="fine">Fine</option>
                    <option value="late_fee">Late Fee</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={feeForm.amount}
                    onChange={(e) => setFeeForm({...feeForm, amount: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <input
                    type="date"
                    required
                    value={feeForm.due_date}
                    onChange={(e) => setFeeForm({...feeForm, due_date: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  rows="3"
                  value={feeForm.description}
                  onChange={(e) => setFeeForm({...feeForm, description: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Explain what this fee is for and any additional details..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Apply To</label>
                <div className="space-y-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="apply_to"
                      value="all"
                      checked={feeForm.apply_to === 'all'}
                      onChange={(e) => setFeeForm({...feeForm, apply_to: e.target.value})}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">All Members</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="apply_to"
                      value="selected"
                      checked={feeForm.apply_to === 'selected'}
                      onChange={(e) => setFeeForm({...feeForm, apply_to: e.target.value})}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">Selected Members (Coming Soon)</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateFeeModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Fee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REMOVED: Board member personal payment section - now uses unified "Your Balance" section above */}

      {/* Create Payment Modal */}
      {showCreatePayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New Payment</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPayment.user_id}
                  onChange={(e) => setNewPayment({...newPayment, user_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">-- Select Homeowner, Board Member, or Team Member --</option>
                  {allUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role}) - {user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPayment.payment_type}
                  onChange={(e) => setNewPayment({...newPayment, payment_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="dues">Dues</option>
                  <option value="fine">Fine</option>
                  <option value="event">Event</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newPayment.due_date}
                    onChange={(e) => setNewPayment({...newPayment, due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description/Details
                </label>
                <textarea
                  value={newPayment.description}
                  onChange={(e) => setNewPayment({...newPayment, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter details about this payment..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreatePayment(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePayment}
                disabled={uploading}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50"
              >
                {uploading ? 'Creating...' : 'Create Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Configuration Panel Component
const PaymentConfigurationPanel = ({ config, setConfig, onSave, onCancel, loading }) => {
  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const togglePaymentMethod = (method) => {
    const current = config.payment_methods_enabled || [];
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    updateConfig('payment_methods_enabled', updated);
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-6">Payment Configuration</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payment Methods */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Payment Methods</h4>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.online_payment_enabled}
                onChange={(e) => updateConfig('online_payment_enabled', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Online Credit/Debit Card Payments</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.check_payment_enabled}
                onChange={(e) => updateConfig('check_payment_enabled', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Check Payments</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.money_order_enabled}
                onChange={(e) => updateConfig('money_order_enabled', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Money Order Payments</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.external_payment_enabled}
                onChange={(e) => updateConfig('external_payment_enabled', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Public external webpage (no account needed)</span>
            </label>
          </div>
        </div>

        {/* Dues Configuration */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Automatic Dues Configuration</h4>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.automatic_dues_enabled}
                onChange={(e) => updateConfig('automatic_dues_enabled', e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-green-800">
                ✅ Enable Automatic Dues Creation
              </span>
            </div>
            <p className="text-xs text-green-700 mt-1 ml-6">
              Dues will be automatically created based on your schedule - no manual "Create Monthly Dues" needed!
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dues Amount ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={config.dues_amount}
              onChange={(e) => updateConfig('dues_amount', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={config.dues_frequency}
              onChange={(e) => updateConfig('dues_frequency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly (Every 3 months)</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {config.dues_frequency === 'yearly' ? 'Due Date' : `Due Day of ${config.dues_frequency === 'monthly' ? 'Month' : 'Quarter'}`}
            </label>
            
            {config.dues_frequency === 'yearly' ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Month</label>
                  <select
                    value={config.due_month || 1}
                    onChange={(e) => updateConfig('due_month', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Day</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={config.due_day || 1}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      const num = val === '' ? 1 : Math.max(1, Math.min(31, parseInt(val)));
                      updateConfig('due_day', num);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 col-span-2">
                  Select the month and day when yearly dues are due
                </p>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={config.due_day || 1}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 1 : Math.max(1, Math.min(31, parseInt(val)));
                    updateConfig('due_day', num);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {config.dues_frequency === 'monthly' && 'Day 1-31 of each month'}
                  {config.dues_frequency === 'quarterly' && 'Day of month in Jan, Apr, Jul, Oct'}
                </p>
              </>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-xs text-gray-500">(When to begin charging dues)</span>
            </label>
            <input
              type="date"
              value={config.dues_start_date || ''}
              onChange={(e) => updateConfig('dues_start_date', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Optional: Set a specific date to start dues cycle. If not set, dues will start on the next due date based on your frequency.
            </p>
          </div>
        </div>

        {/* Mail Payment Configuration */}
        {(config.check_payment_enabled || config.money_order_enabled) && (
          <div className="col-span-2 space-y-4">
            <h4 className="font-medium text-gray-900">Mail Payment Information</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mailing Address</label>
              <textarea
                value={config.mail_payment_address || ''}
                onChange={(e) => updateConfig('mail_payment_address', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter the address where payments should be mailed..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Instructions</label>
              <textarea
                value={config.mail_payment_instructions || ''}
                onChange={(e) => updateConfig('mail_payment_instructions', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter instructions for check/money order payments..."
              />
            </div>
          </div>
        )}

        {/* Late Fees & Interest Configuration Note */}
        <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">Configure Late Fees & Interest in Ledger</h4>
              <p className="text-sm text-blue-700">
                Late fee and interest rules should be configured in the <strong>Ledger</strong> tab under "Configure Late Fee & Interest Rules". 
                You'll be able to set amounts, timing, and see automatic calculations based on your dues configuration.
              </p>
            </div>
          </div>
        </div>

        {/* Autopay Configuration */}
        <div className="col-span-2 space-y-4">
          <h4 className="font-medium text-gray-900">Autopay Settings</h4>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.autopay_enabled}
              onChange={(e) => updateConfig('autopay_enabled', e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Enable Autopay for Homeowners</span>
          </label>
          
          {config.autopay_enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Autopay Discount (%)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={config.autopay_discount_percent}
                onChange={(e) => updateConfig('autopay_discount_percent', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter discount percentage for autopay users"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Offer a discount for homeowners who set up autopay
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 mt-6">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={loading}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

// Overview Component
const PaymentOverview = ({ config, summary, transactions, isBoard, user, onPayOnline, setActiveView, preferredPaymentMethod, setPreferredPaymentMethod }) => {
  const { activeHoaId } = useMultiHOA();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const userPendingPayments = transactions.filter(t => 
    user && t.user_id === user.id && t.payment_status === 'pending'
  );

  // State for homeowner dues
  const [homeownerDues, setHomeownerDues] = React.useState(null);
  const [loadingDues, setLoadingDues] = React.useState(true);
  const [unpaidBills, setUnpaidBills] = React.useState(null);
  const [loadingUnpaid, setLoadingUnpaid] = React.useState(false);

  // Handle payment success redirect from Stripe
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('payment_success');
    
    if (sessionId) {
      // CRITICAL: Must call backend to send email and post transactions
      const processPayment = async () => {
        try {
          console.log('✅ Payment success - calling backend to process:', sessionId);
          const response = await api.get(`/api/stripe/express/payment/success?session_id=${sessionId}`);
          
          console.log('Backend response:', response.data);
          
          // Show success message
          const isACH = response.data.payment_status === 'processing';
          const message = isACH 
            ? `✅ Payment Submitted!\n\nYour bank payment has been submitted successfully.\n\nYou'll receive a confirmation email shortly.\n\nRefreshing page...`
            : `✅ Payment Successful!\n\nYour payment has been processed.\n\nYou'll receive a confirmation email shortly.\n\nRefreshing page...`;
          
          alert(message);
          // Refresh page to show updated balances
          window.location.href = window.location.pathname;
        } catch (error) {
          console.error('Backend call failed but showing success:', error);
          // Still show success - payment is valid
          alert('✅ Payment Submitted!\n\nYour payment has been sent to Stripe.\n\nRefreshing page...');
          window.location.href = window.location.pathname;
        }
      };
      
      processPayment();
    }
    
    const canceled = urlParams.get('payment_canceled');
    if (canceled) {
      alert('Payment was canceled.');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Debug: Log unpaidBills state changes
  React.useEffect(() => {
    console.log('[DEBUG] unpaidBills state updated:', unpaidBills);
  }, [unpaidBills]);

  // Fetch homeowner's ledger data
  React.useEffect(() => {
    const fetchHomeownerDues = async () => {
      // FIXED: Board members also have properties and personal dues!
      // Don't skip fetching for board members
      
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        if (!currentHoaId) {
          setLoadingDues(false);
          return;
        }
        
        // Fetch unpaid bills first (this is what we need for payments)
        try {
          console.log('[DEBUG] Fetching unpaid bills...');
          const unpaidResponse = await api.get('/api/stripe/express/payment/unpaid-bills');
          console.log('[DEBUG] Unpaid bills response:', unpaidResponse);
          console.log('[DEBUG] Unpaid bills data:', unpaidResponse.data);
          if (unpaidResponse.data.success) {
            setUnpaidBills(unpaidResponse.data);
            console.log('✅ Unpaid bills loaded:', unpaidResponse.data);
            console.log('  - total_amount:', unpaidResponse.data.total_amount);
            console.log('  - items_count:', unpaidResponse.data.items_count);
            console.log('  - items:', unpaidResponse.data.items);
          } else {
            console.warn('[DEBUG] Unpaid bills success=false:', unpaidResponse.data);
          }
        } catch (unpaidError) {
          console.error('[DEBUG] Failed to fetch unpaid bills - ERROR:', unpaidError);
          console.error('[DEBUG] Error response:', unpaidError.response);
        }

        // Also fetch ledger summary for backward compatibility
        try {
          const propertyAddress = user.property_address || user.address || '';
          if (propertyAddress) {
            const summaryResponse = await api.get(`/api/ledger/${currentHoaId}/ledger/${encodeURIComponent(propertyAddress)}`);
            if (summaryResponse.data && summaryResponse.data.ledger) {
              const ledger = summaryResponse.data.ledger;
              setHomeownerDues({
                balance_due: ledger.balance_due || 0,
                current_dues: ledger.current_dues || 0,
                next_due_date: ledger.next_due_date
              });
            }
          }
        } catch (summaryError) {
          console.error('Failed to fetch homeowner dues summary:', summaryError);
        }
        
      } catch (error) {
        console.error('Failed to fetch homeowner dues:', error);
      } finally {
        setLoadingDues(false);
      }
    };
    
    fetchHomeownerDues();
  }, [user, isBoard, activeHoaId]);

  // Fetch processing payments
  const [processingPayments, setProcessingPayments] = React.useState([]);
  React.useEffect(() => {
    const fetchProcessingPayments = async () => {
      try {
        const response = await api.get('/api/stripe/express/payment/processing-payments');
        if (response.data.success) {
          setProcessingPayments(response.data.processing_payments || []);
        }
      } catch (error) {
        console.error('Failed to fetch processing payments:', error);
      }
    };
    
    if (user && !isBoard) {
      fetchProcessingPayments();
      // Poll every 30 seconds to check if payment completed
      const interval = setInterval(fetchProcessingPayments, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isBoard]);


  // Fetch unpaid bills for "Pay All" feature
  const fetchUnpaidBills = async () => {
    try {
      setLoadingUnpaid(true);
      const response = await api.get('/api/stripe/express/payment/unpaid-bills');
      if (response.data.success) {
        setUnpaidBills(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch unpaid bills:', error);
      alert('Failed to load unpaid bills');
    } finally {
      setLoadingUnpaid(false);
    }
  };

  // Handle pay single item - uses preferred payment method
  const handlePaySingleItem = async (items) => {
    try {
      setLoadingUnpaid(true);
      const response = await api.post('/api/stripe/express/payment/create-session', {
        items: items,
        payment_method: preferredPaymentMethod // Use the toggle selection
      });

      if (response.data.success) {
        // Redirect to Stripe checkout
        window.location.href = response.data.session_url;
      } else {
        alert('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment session creation failed:', error);
      
      if (error.response?.status === 404 || error.response?.data?.detail?.includes('Express account not found')) {
        alert('HOA has not set up payment processing yet. Please contact your board.');
      } else {
        alert(error.response?.data?.detail || 'Failed to create payment session.');
      }
    } finally {
      setLoadingUnpaid(false);
    }
  };

  // Handle pay all bills
  const handlePayAllBills = async () => {
    if (!unpaidBills || unpaidBills.items_count === 0) {
      alert('No unpaid bills found');
      return;
    }

    // Confirm payment
    const confirmed = confirm(
      `Pay All Outstanding Bills?\n\n` +
      `Total Amount: $${unpaidBills.total_amount.toFixed(2)}\n` +
      `Number of Items: ${unpaidBills.items_count}\n\n` +
      `You will be redirected to Stripe to complete the payment.`
    );

    if (!confirmed) return;

    try {
      setLoadingUnpaid(true);
      const response = await api.post('/api/stripe/express/payment/create-session', {
        items: unpaidBills.items,
        payment_method: preferredPaymentMethod // Use the toggle selection
      });

      if (response.data.success) {
        // Redirect to Stripe checkout
        window.location.href = response.data.session_url;
      } else {
        alert('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment session creation failed:', error);
      
      // Check if Stripe Express is not set up
      if (error.response?.status === 404 || error.response?.data?.detail?.includes('Express account not found')) {
        alert('HOA has not set up payment processing yet. Please contact your board to set up Stripe Express in the Payment Setup tab.');
      } else {
        alert(error.response?.data?.detail || 'Failed to create payment session. Please try again.');
      }
    } finally {
      setLoadingUnpaid(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Processing Payment Banner */}
      {processingPayments.length > 0 && !isBoard && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400 animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">Payment Processing</h3>
              <div className="mt-2 text-sm text-blue-700">
                {processingPayments.map((payment, idx) => (
                  <div key={idx} className="mb-2">
                    <strong>⏳ Bank payment in progress</strong> - ${(payment.amount / 100).toFixed(2)}
                    <br />
                    <span className="text-xs">Processing started: {new Date(payment.created_at || payment.created).toLocaleString()}</span>
                    <br />
                    <span className="text-xs text-blue-600">Bank transfers take 2-5 business days to complete. Please do not submit another payment.</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Summary Cards for Board */}
      {isBoard && summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">💰</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Collected</dt>
                    <dd className="text-lg font-medium text-gray-900">{formatCurrency(summary.total_paid)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">⏳</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">{formatCurrency(summary.total_pending)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">⚠️</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Overdue</dt>
                    <dd className="text-lg font-medium text-gray-900">{formatCurrency(summary.total_overdue)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">🏠</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Paid Homeowners</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.homeowners_paid}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Card - Shows for ALL users who have balance */}
      {(
        <div className="bg-white border border-gray-200 rounded-lg shadow">
          <div className="p-4">
            {loadingDues ? (
              <div className="text-center py-4">
                <div className="text-gray-600 text-sm">Loading your payment information...</div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">💳 Your Balance</h3>
                    <p className="text-xs text-gray-600 mt-1">
                      {unpaidBills?.items_count || 0} {(unpaidBills?.items_count || 0) === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(unpaidBills?.total_amount || homeownerDues?.balance_due || 0)}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">
                      TOTAL DUE
                    </div>
                  </div>
                </div>

                {/* Payment Method Selector */}
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    💳 Select Payment Method:
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreferredPaymentMethod('bank')}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                        preferredPaymentMethod === 'bank'
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-lg mb-1">🏦</span>
                        <span>Bank (ACH)</span>
                        <span className="text-xs mt-1 opacity-90">0.8% fee (max $5)</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setPreferredPaymentMethod('card')}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                        preferredPaymentMethod === 'card'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-lg mb-1">💳</span>
                        <span>Card</span>
                        <span className="text-xs mt-1 opacity-90">2.9% + $0.30</span>
                      </div>
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    {preferredPaymentMethod === 'bank' 
                      ? '✓ Lowest fee option - Processing takes 3-5 business days' 
                      : 'Instant processing - Higher processing fee'}
                  </p>
                </div>

            {/* Individual Unpaid Items */}
            {unpaidBills && unpaidBills.items && unpaidBills.items.length > 0 && (
              <div className="mb-3">
                <div className="bg-gray-50 rounded border border-gray-200 p-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Items:</h4>
                  <div className="space-y-1">
                    {unpaidBills.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-1.5 border-b border-gray-200 last:border-b-0">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.description}</div>
                        {item.due_date && (
                          <div className="text-xs text-gray-500">Due: {new Date(item.due_date).toLocaleDateString()}</div>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <div className={`text-sm font-semibold ${item.amount < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {formatCurrency(item.amount)}
                        </div>
                        {item.amount > 0 && (
                          <button
                            onClick={() => {
                              const singleItem = [{
                                description: item.description,
                                amount: item.amount,
                                ledger_id: item.ledger_id
                              }];
                              handlePaySingleItem(singleItem);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Pay
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}
            
                <div>
                  <button
                    onClick={handlePayAllBills}
                    disabled={loadingUnpaid || (unpaidBills?.items_count === 0 && homeownerDues?.balance_due === 0)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                  >
                    <span>💰</span>
                    <span>
                      {loadingUnpaid 
                        ? 'Loading...' 
                        : `Pay All (${formatCurrency(unpaidBills?.total_amount || homeownerDues?.balance_due || 0)})`
                      }
                    </span>
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Secure payment via Stripe
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Show message if no unpaid bills */}
      {!loadingDues && unpaidBills && unpaidBills.items_count === 0 && (unpaidBills.total_amount === 0 || unpaidBills.total_amount === undefined) && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
          <div className="text-4xl mb-2">✅</div>
          <h3 className="text-lg font-bold text-green-900 mb-1">All Caught Up!</h3>
          <p className="text-sm text-green-700">You have no outstanding payments.</p>
        </div>
      )}

      {/* Payment Information */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Payment Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Current Dues</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                {config?.automatic_dues_enabled && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-2 mb-3">
                    <div className="flex items-center text-sm text-green-800">
                      <span className="text-green-500 mr-2">✅</span>
                      <span className="font-medium">Automatic Dues Enabled</span>
                    </div>
                    <p className="text-xs text-green-700 mt-1 ml-6">
                      Dues are automatically created {config.dues_frequency} - no manual action needed!
                    </p>
                  </div>
                )}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="font-medium">{formatCurrency(config?.dues_amount || 0)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Frequency:</span>
                  <span className="font-medium capitalize">{config?.dues_frequency || 'Monthly'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Due Day:</span>
                  <span className="font-medium">
                    {config?.dues_frequency === 'yearly' 
                      ? `${getMonthName(config?.due_month || 1)} ${config?.due_day || 1}${getOrdinalSuffix(config?.due_day || 1)}`
                      : `${config?.due_day || 1}${getOrdinalSuffix(config?.due_day || 1)} of ${config?.dues_frequency === 'monthly' ? 'month' : 'quarter'}`
                    }
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">Payment Methods</h4>
              <div className="space-y-2">
                {config?.online_payment_enabled && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Online Credit/Debit Card
                  </div>
                )}
                {config?.check_payment_enabled && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Check Payments
                  </div>
                )}
                {config?.money_order_enabled && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Money Order
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* REMOVED: Old mock data section - now handled by unified payment card above */}

          {/* Mail Payment Instructions */}
          {(config?.check_payment_enabled || config?.money_order_enabled) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-4">Mail Payment Instructions</h4>
              
              {config?.mail_payment_address && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h5 className="font-medium text-blue-900 mb-2">Mailing Address:</h5>
                  <pre className="text-sm text-blue-800 whitespace-pre-wrap">{config.mail_payment_address}</pre>
                </div>
              )}
              
              {config?.mail_payment_instructions && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Payment Instructions:</h5>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{config.mail_payment_instructions}</p>
                </div>
              )}
            </div>
          )}

          {/* Autopay Setup for Homeowners */}
          {!isBoard && config?.autopay_enabled && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">
                  💳 Set Up Autopay {config?.autopay_discount_percent > 0 && `(Save ${config.autopay_discount_percent}%!)`}
                </h4>
                <p className="text-sm text-blue-800 mb-4">
                  Never miss a payment! Set up automatic payments for your HOA dues.
                  {config?.autopay_discount_percent > 0 && ` Plus, save ${config.autopay_discount_percent}% on all future payments!`}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setActiveView('autopay-setup')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Set Up Autopay
                  </button>
                  <button
                    onClick={() => alert('Learn more: Autopay automatically charges your card each month on the due date. You can cancel anytime.')}
                    className="border border-blue-300 text-blue-700 hover:bg-blue-100 px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Payment Management Component with Enhanced Filters
const PaymentManagement = ({ summary, transactions, onCreateSpecialFee, onCreatePayment, onSendReminders, onShowManualPayment, onExportReport, overdueCount = 0, loading, paymentFilters, setPaymentFilters, residents, applyFilters, sendTargetedReminders }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const overdueTransactions = transactions.filter(t => {
    if (t.payment_status === 'paid' || !t.due_date) return false;
    return new Date(t.due_date) < new Date();
  });

  const filteredResidents = applyFilters(residents);

  return (
    <div className="space-y-6">
      {/* Enhanced Filter Controls */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Payment Filters & Controls</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                value={paymentFilters.status}
                onChange={(e) => setPaymentFilters({...paymentFilters, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Residents</option>
                <option value="paid">Paid ({residents.filter(r => r.balance === 0).length})</option>
                <option value="unpaid">Unpaid ({residents.filter(r => r.balance > 0).length})</option>
                <option value="overdue">Overdue ({residents.filter(r => {
                  const daysSince = (new Date() - new Date(r.lastPayment)) / (1000 * 60 * 60 * 24);
                  return r.balance > 0 && daysSince > 30;
                }).length})</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={paymentFilters.dateRange}
                onChange={(e) => setPaymentFilters({...paymentFilters, dateRange: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Time</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
              </select>
            </div>

            {/* Reminder Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Type</label>
              <select
                id="reminderType"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="overdue">Overdue Payments</option>
                <option value="upcoming">Upcoming Due</option>
                <option value="all_unpaid">All Unpaid</option>
              </select>
            </div>

            {/* Quick Action */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  const reminderType = document.getElementById('reminderType').value;
                  sendTargetedReminders(reminderType);
                }}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
              >
                📧 Send Reminders
              </button>
            </div>
          </div>

          {/* Filter Results Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">
                  Filtered Results: {filteredResidents.length} of {residents.length} residents
                </h4>
                <p className="text-sm text-gray-600">
                  Total Outstanding: ${filteredResidents.reduce((sum, r) => sum + r.balance, 0).toFixed(2)}
                </p>
              </div>
              <div>
                <button
                  onClick={() => setPaymentFilters({status: 'all', dateRange: 'all', resident: 'all'})}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Management Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Payment Management Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <button
              onClick={onCreatePayment}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium disabled:opacity-50"
            >
              ➕ Create Payment
            </button>
            
            {/* MANUAL PAYMENT ENTRY - REMOVED - USE LEDGER TAB INSTEAD
            <button
              onClick={onShowManualPayment}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-md font-medium disabled:opacity-50"
            >
              💰 Manual Payment Entry
            </button>
            */}
            
            {/* SEND OVERDUE REMINDERS - TEMPORARILY DISABLED - EMAIL FEATURE NOT IMPLEMENTED YET
            <button
              onClick={onSendReminders}
              disabled={loading || overdueTransactions.length === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-md font-medium disabled:opacity-50"
            >
              📧 Send Overdue Reminders ({overdueCount})
            </button>
            */}
            
            <button
              onClick={() => {
                if (window.confirm('Generate Dues for All Properties?\n\nThis will create new dues entries based on the configured amount and frequency in the Payment Configuration. Use this to:\n• Start the dues system for the first time\n• Update dues after changing the amount in Configuration\n\nAfter this, the system will automatically generate dues based on your frequency (monthly/quarterly/yearly) until you change the configuration again.')) {
                  // Call the generate dues handler passed from parent
                  if (window.onGenerateDues) {
                    window.onGenerateDues();
                  } else {
                    alert('Please contact support to configure this feature.');
                  }
                }
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-md font-medium"
            >
              🔄 Generate Dues
            </button>
            
            <button
              onClick={onExportReport}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-md font-medium"
            >
              📊 Export CSV Report
            </button>
          </div>
        </div>
      </div>

      {/* Payment Status Overview */}
      {summary && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Collection Status</h3>
            {/* Debug: Show raw summary data */}
            {console.log('[Collection Status] Summary data:', summary)}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Financial Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Collected:</span>
                    <span className="font-medium text-green-600">{formatCurrency(summary.total_paid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-medium text-yellow-600">{formatCurrency(summary.total_pending)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Overdue:</span>
                    <span className="font-medium text-red-600">{formatCurrency(summary.total_overdue)}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Total Outstanding:</span>
                      <span>{formatCurrency(summary.total_due)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-4">Homeowner Status</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid:</span>
                    <span className="font-medium text-green-600">{summary.homeowners_paid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-medium text-yellow-600">{summary.homeowners_pending}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Overdue:</span>
                    <span className="font-medium text-red-600">{summary.homeowners_overdue}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Manual Payment Manager Component
const ManualPaymentManager = ({ transactions, onRefresh }) => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [treasurerNotes, setTreasurerNotes] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [newPayment, setNewPayment] = useState({
    user_id: '',
    payment_type: 'dues',
    amount: '',
    due_date: new Date().toISOString().split('T')[0],
    description: ''
  });
  
  // Filter state
  const [filterTransactionType, setFilterTransactionType] = useState('all');
  const [filterProperty, setFilterProperty] = useState('all');
  const [searchText, setSearchText] = useState('');
  
  // Debug: Log transaction data to see structure
  React.useEffect(() => {
    if (transactions.length > 0) {
      console.log('Sample transaction:', transactions[0]);
      console.log('Total transactions:', transactions.length);
    }
  }, [transactions]);

  // Get unique properties for filters
  const uniqueProperties = [...new Set(transactions.map(t => t.user_name || t.property_address).filter(Boolean))].sort();

  // Apply filters
  const filteredTransactions = transactions.filter(t => {
    // Filter by transaction type (dues, event, penalty, interest, etc.)
    // Check description field to determine type since transactions may not have explicit type field
    if (filterTransactionType !== 'all') {
      const description = (t.description || '').toLowerCase();
      const transactionType = t.payment_type || t.transaction_type || '';
      
      let matches = false;
      
      // Match by explicit type if available
      if (transactionType === filterTransactionType) {
        matches = true;
      }
      // Match by description keywords
      else if (filterTransactionType === 'dues' && (description.includes('dues') || description.includes('monthly'))) {
        matches = true;
      }
      else if (filterTransactionType === 'event' && description.includes('event')) {
        matches = true;
      }
      else if (filterTransactionType === 'late_fee' && (description.includes('late') || description.includes('fee'))) {
        matches = true;
      }
      else if (filterTransactionType === 'interest' && description.includes('interest')) {
        matches = true;
      }
      else if (filterTransactionType === 'fine' && (description.includes('fine') || description.includes('penalty') || description.includes('violation'))) {
        matches = true;
      }
      else if (filterTransactionType === 'violation' && description.includes('violation')) {
        matches = true;
      }
      else if (filterTransactionType === 'balance_forward' && description.includes('balance forward')) {
        matches = true;
      }
      else if (filterTransactionType === 'other' && !description.includes('dues') && !description.includes('event') && !description.includes('late') && !description.includes('interest') && !description.includes('fine') && !description.includes('violation')) {
        matches = true;
      }
      
      if (!matches) {
        return false;
      }
    }
    
    // Filter by property
    if (filterProperty !== 'all') {
      const property = t.user_name || t.property_address || '';
      if (property !== filterProperty) {
        return false;
      }
    }
    
    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesName = (t.user_name || '').toLowerCase().includes(searchLower);
      const matchesEmail = (t.user_email || '').toLowerCase().includes(searchLower);
      const matchesProperty = (t.property_address || '').toLowerCase().includes(searchLower);
      const matchesDescription = (t.description || '').toLowerCase().includes(searchLower);
      
      if (!matchesName && !matchesEmail && !matchesProperty && !matchesDescription) {
        return false;
      }
    }
    
    return true;
  });

  // Debug: Log all transactions and their statuses
  React.useEffect(() => {
    console.log('=== Manual Payment Manager Debug ===');
    console.log('Total transactions received:', transactions.length);
    console.log('Filtered transactions:', filteredTransactions.length);
    console.log('Transaction statuses:', transactions.map(t => ({
      name: t.user_name,
      status: t.payment_status,
      amount: t.amount,
      description: t.description
    })));
  }, [transactions, filteredTransactions]);

  const pendingTransactions = filteredTransactions.filter(t => 
    t.payment_status === 'pending' || t.payment_status === 'unpaid'
  );
  
  console.log('Pending transactions after filter:', pendingTransactions.length, pendingTransactions);

  // Load all users (homeowners, board members, team members)
  useEffect(() => {
    loadAllUsers();
  }, []);

  const loadAllUsers = async () => {
    try {
      const response = await axios.get('/api/hoa/users-all');
      if (response.data.success) {
        setAllUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleCreatePayment = async () => {
    try {
      if (!newPayment.user_id || !newPayment.amount || !newPayment.due_date) {
        alert('Please fill in all required fields');
        return;
      }

      setUploading(true);

      const paymentData = {
        user_id: newPayment.user_id,
        payment_type: newPayment.payment_type,
        amount: parseFloat(newPayment.amount),
        due_date: newPayment.due_date,
        description: newPayment.description || `${newPayment.payment_type} payment`
      };

      const response = await axios.post('/api/board/payment/create-manual', paymentData);
      
      if (response.data.success) {
        alert('Payment created successfully!');
        setShowCreatePayment(false);
        setNewPayment({
          user_id: '',
          payment_type: 'dues',
          amount: '',
          due_date: new Date().toISOString().split('T')[0],
          description: ''
        });
        await onRefresh();
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('Failed to create payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedTransaction) return;

    try {
      setUploading(true);
      
      let photoProofUrl = null;
      if (proofFile) {
        const formData = new FormData();
        formData.append('file', proofFile);
        
        const uploadResponse = await axios.post('/api/board/payment/upload-proof', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (uploadResponse.data.success) {
          photoProofUrl = uploadResponse.data.file_url;
        }
      }

      const markPaidData = {
        payment_method: paymentMethod,
        treasurer_notes: treasurerNotes,
        photo_proof_url: photoProofUrl
      };

      await axios.post(`/api/board/payment/${selectedTransaction.id}/mark-paid`, markPaidData);
      
      // Reset form
      setSelectedTransaction(null);
      setPaymentMethod('check');
      setTreasurerNotes('');
      setProofFile(null);
      
      // Refresh data
      await onRefresh();
      
      alert('Payment marked as paid successfully!');
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      alert('Failed to mark payment as paid');
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Create Payment Button */}
      <div className="bg-white shadow rounded-lg p-6">
        <button
          onClick={() => setShowCreatePayment(true)}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-md font-medium"
        >
          ➕ Create New Payment
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filter Payments</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Transaction Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Type
            </label>
            <select
              value={filterTransactionType}
              onChange={(e) => setFilterTransactionType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Types</option>
              <option value="dues">Dues</option>
              <option value="event">Event</option>
              <option value="late_fee">Late Fee</option>
              <option value="interest">Interest</option>
              <option value="fine">Fine/Penalty</option>
              <option value="violation">Violation</option>
              <option value="balance_forward">Balance Forward</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Property Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property / Homeowner
            </label>
            <select
              value={filterProperty}
              onChange={(e) => setFilterProperty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Properties</option>
              {uniqueProperties.map(property => (
                <option key={property} value={property}>{property}</option>
              ))}
            </select>
          </div>

          {/* Search Box */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Name, email, property..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Clear Filters Button */}
        {(filterTransactionType !== 'all' || filterProperty !== 'all' || searchText) && (
          <div className="mt-4">
            <button
              onClick={() => {
                setFilterTransactionType('all');
                setFilterProperty('all');
                setSearchText('');
              }}
              className="text-sm text-indigo-600 hover:text-indigo-800"
            >
              ✕ Clear All Filters
            </button>
          </div>
        )}

        {/* Results count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredTransactions.length} of {transactions.length} transactions
          {pendingTransactions.length > 0 && ` (${pendingTransactions.length} pending)`}
        </div>
      </div>

      {/* Pending Payments List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Pending Payments</h3>
          <p className="text-sm text-gray-600 mt-1">Mark payments as paid when received via check or money order</p>
        </div>
        <div className="p-6">
          {pendingTransactions.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No pending payments</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingTransactions.map((transaction) => (
                <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {transaction.user_name || 'Unknown User'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {transaction.property_address || transaction.user_email}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {transaction.description} • {formatCurrency(transaction.amount)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Due: {new Date(transaction.due_date).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedTransaction(transaction)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm font-medium"
                    >
                      Mark Paid
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mark Payment Paid Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Mark Payment as Paid</h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{selectedTransaction.user_name}</p>
              <p className="text-sm text-gray-600">{selectedTransaction.description}</p>
              <p className="text-sm font-medium">{formatCurrency(selectedTransaction.amount)}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="check">Check</option>
                  <option value="money_order">Money Order</option>
                  <option value="cash">Cash</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Treasurer Notes</label>
                <textarea
                  value={treasurerNotes}
                  onChange={(e) => setTreasurerNotes(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Add any notes about this payment..."
                />
              </div>

              {/* COMMENTED OUT: Document upload feature - not visible in history
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Photo Proof (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setProofFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">Upload a photo of the check or money order</p>
              </div>
              */}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setSelectedTransaction(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkPaid}
                disabled={uploading}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
              >
                {uploading ? 'Processing...' : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Payment History Component - Shows completed payments only
const PaymentHistory = ({ transactions, isBoard, user }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paidTransactions, setPaidTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-800 bg-green-100';
      case 'pending': return 'text-yellow-800 bg-yellow-100';
      case 'failed': return 'text-red-800 bg-red-100';
      case 'expired': return 'text-gray-800 bg-gray-100';
      default: return 'text-gray-800 bg-gray-100';
    }
  };

  // Load PAID transactions from API
  useEffect(() => {
    const loadPaidTransactions = async () => {
      try {
        console.log('[Payment History] Fetching paid transactions...');
        console.log('[Payment History] User:', user?.email, 'User ID:', user?.id, 'isBoard:', isBoard);
        console.log('[Payment History] User property_address:', user?.property_address);
        console.log('[Payment History] User address:', user?.address);
        
        const response = await api.get('/api/payment/all-transactions?status_filter=paid');
        if (response.data && response.data.transactions) {
          let allPaidTransactions = response.data.transactions;
          console.log('[Payment History] Total paid transactions from API:', allPaidTransactions.length);
          
          // Debug: Show sample transaction structure
          if (allPaidTransactions.length > 0) {
            console.log('[Payment History] Sample transaction:', allPaidTransactions[0]);
          }
          
          // For homeowners, filter to show only THEIR payments
          if (!isBoard && user) {
            console.log('[Payment History] Filtering for homeowner. Checking against:');
            console.log('  - user.id:', user.id);
            console.log('  - user.email:', user.email);
            console.log('  - user.property_address:', user.property_address);
            console.log('  - user.address:', user.address);
            
            allPaidTransactions = allPaidTransactions.filter(t => {
              const match = t.user_id === user.id || 
                           t.user_email === user.email ||
                           t.property_address === user.property_address ||
                           t.property_address === user.address;
              
              if (match) {
                console.log('[Payment History] MATCHED:', t.description, t.user_id, t.property_address);
              }
              return match;
            });
            console.log('[Payment History] Filtered to user transactions:', allPaidTransactions.length);
          }
          
          setPaidTransactions(allPaidTransactions);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading paid transactions:', error);
        setPaidTransactions([]);
        setLoading(false);
      }
    };
    
    loadPaidTransactions();
  }, [isBoard, user]);

  // Filter paid transactions only
  const filteredTransactions = paidTransactions.filter(transaction => {
    const matchesSearch = !searchTerm || 
      transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.property_address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Calculate statistics from paid transactions
  const totalPaidAmount = filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const onlinePayments = filteredTransactions.filter(t => 
    t.payment_method === 'online' || 
    t.payment_method === 'stripe' || 
    t.transaction_type === 'online_payment'
  ).length;
  const manualPayments = filteredTransactions.filter(t => 
    t.payment_method === 'check' || 
    t.payment_method === 'cash' || 
    (t.payment_method === 'ach' && t.transaction_type !== 'online_payment')
  ).length;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Payment History</h3>
            <p className="text-sm text-gray-600 mt-1">View all completed payments and transactions</p>
            {isBoard && (
              <div className="flex mt-2 space-x-4 text-sm">
                <span className="text-green-600">✅ Total: {filteredTransactions.length} payments</span>
                <span className="text-blue-600">💳 Online: {onlinePayments}</span>
                <span className="text-gray-600">📝 Manual: {manualPayments}</span>
                <span className="text-green-600">💰 Amount: {formatCurrency(totalPaidAmount)}</span>
              </div>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>
      
      
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading payment data...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-3l-7 4-7-4m14 6H5" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No transactions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => (
                <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {isBoard && (
                        <div className="mb-1">
                          <p className="font-medium text-gray-900">
                            {transaction.user_name || 'Unknown User'}
                          </p>
                          {/* Board-Only Property Identifier */}
                          <p className="text-xs text-blue-600 font-medium">
                            🏠 Property: {transaction.property_identifier || transaction.property_address || 'Not specified'}
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-gray-600">
                        {transaction.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>💰 Amount: {formatCurrency(transaction.amount)}</span>
                        {transaction.paid_at && (
                          <span className="text-green-600 font-semibold">
                            ✅ Paid: {new Date(transaction.paid_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </span>
                        )}
                        {transaction.due_date && (
                          <span>📅 Due: {new Date(transaction.due_date).toLocaleDateString()}</span>
                        )}
                      </div>
                      {isBoard && transaction.treasurer_notes && (
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Notes:</strong> {transaction.treasurer_notes}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.payment_status || 'paid')}`}>
                        {transaction.payment_status || 'paid'}
                      </span>
                      <span className="text-xs font-medium text-gray-700 capitalize bg-gray-100 px-2.5 py-0.5 rounded">
                        {transaction.payment_method === 'check' && '📝 Check'}
                        {transaction.payment_method === 'cash' && '💵 Cash'}
                        {transaction.payment_method === 'ach' && '🏦 ACH'}
                        {(transaction.payment_method === 'online' || transaction.payment_method === 'stripe') && '💳 Online/Stripe'}
                        {!['check', 'cash', 'ach', 'online', 'stripe'].includes(transaction.payment_method) && (transaction.payment_method || 'N/A')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};

// Autopay Setup Component
const AutopaySetup = ({ config, user, onBack, onComplete }) => {
  const [setupForm, setSetupForm] = useState({
    enableAutopay: true,
    paymentMethod: 'card',
    billingFrequency: config?.dues_frequency || 'monthly',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardHolderName: user?.name || '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    agreeToTerms: false,
    savePaymentMethod: true,
    notificationEmail: user?.email || ''
  });

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Payment Method, 2: Billing Details, 3: Confirm

  const handleInputChange = (field, value) => {
    setSetupForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!setupForm.agreeToTerms) {
      alert('Please agree to the terms and conditions.');
      return;
    }

    setLoading(true);
    try {
      // In production, this would:
      // 1. Validate card details with Stripe
      // 2. Create a customer and payment method
      // 3. Set up recurring subscription
      // 4. Store autopay preferences in database
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For demo purposes, we'll just complete the setup
      onComplete();
    } catch (error) {
      alert('Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculateSavings = () => {
    if (!config?.autopay_discount_percent) return 0;
    return (config.dues_amount * (config.autopay_discount_percent / 100));
  };

  const getDiscountedAmount = () => {
    return config?.dues_amount - calculateSavings();
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Set Up Autopay</h3>
            <p className="text-sm text-gray-600">Never miss a payment - set up automatic HOA dues</p>
          </div>
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress indicator */}
        <div className="mt-4">
          <div className="flex items-center">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNum ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > stepNum ? '✓' : stepNum}
                </div>
                {stepNum < 3 && (
                  <div className={`w-12 h-1 mx-2 ${
                    step > stepNum ? 'bg-indigo-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>Payment Method</span>
            <span>Billing Details</span>
            <span>Confirm Setup</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Savings Banner */}
        {config?.autopay_discount_percent > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Save {config.autopay_discount_percent}% with Autopay!
                </p>
                <p className="text-xs text-green-700">
                  Pay {formatCurrency(getDiscountedAmount())} instead of {formatCurrency(config.dues_amount)} 
                  (Save {formatCurrency(calculateSavings())} per payment)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Payment Method */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Payment Method</h4>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="card"
                    name="paymentMethod"
                    value="card"
                    checked={setupForm.paymentMethod === 'card'}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="card" className="ml-3 block text-sm font-medium text-gray-700">
                    Credit/Debit Card
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="radio"
                    id="bank"
                    name="paymentMethod"
                    value="bank"
                    checked={setupForm.paymentMethod === 'bank'}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="bank" className="ml-3 block text-sm font-medium text-gray-700">
                    Bank Account (ACH) - Lower fees
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Frequency</label>
                <select
                  value={setupForm.billingFrequency}
                  onChange={(e) => handleInputChange('billingFrequency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Billing Details */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                {setupForm.paymentMethod === 'card' ? 'Card Details' : 'Bank Account Details'}
              </h4>
              
              {setupForm.paymentMethod === 'card' ? (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      value={setupForm.cardNumber}
                      onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                      <select
                        value={setupForm.expiryMonth}
                        onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">MM</option>
                        {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                          <option key={month} value={month.toString().padStart(2, '0')}>
                            {month.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                      <select
                        value={setupForm.expiryYear}
                        onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">YYYY</option>
                        {Array.from({length: 10}, (_, i) => new Date().getFullYear() + i).map(year => (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                      <input
                        type="text"
                        placeholder="123"
                        value={setupForm.cvv}
                        onChange={(e) => handleInputChange('cvv', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={setupForm.cardHolderName}
                      onChange={(e) => handleInputChange('cardHolderName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    Bank account setup will redirect you to a secure banking portal to link your account.
                  </p>
                </div>
              )}

              {/* Billing Address */}
              <div className="mt-6">
                <h5 className="text-md font-medium text-gray-900 mb-3">Billing Address</h5>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      placeholder="123 Main St"
                      value={setupForm.billingAddress}
                      onChange={(e) => handleInputChange('billingAddress', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        placeholder="Austin"
                        value={setupForm.billingCity}
                        onChange={(e) => handleInputChange('billingCity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        value={setupForm.billingState}
                        onChange={(e) => handleInputChange('billingState', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select State</option>
                        <option value="TX">Texas</option>
                        <option value="CA">California</option>
                        <option value="FL">Florida</option>
                        {/* Add more states as needed */}
                      </select>
                    </div>
                  </div>
                  
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <input
                      type="text"
                      placeholder="12345"
                      value={setupForm.billingZip}
                      onChange={(e) => handleInputChange('billingZip', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-md font-medium"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md font-medium"
              >
                Review
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm Setup */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Confirm Autopay Setup</h4>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Method:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {setupForm.paymentMethod === 'card' ? `Card ending in ${setupForm.cardNumber.slice(-4)}` : 'Bank Account'}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Billing Frequency:</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{setupForm.billingFrequency}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Amount per Payment:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(config?.autopay_discount_percent > 0 ? getDiscountedAmount() : config?.dues_amount || 0)}
                    {config?.autopay_discount_percent > 0 && (
                      <span className="text-green-600 text-xs ml-1">
                        ({config.autopay_discount_percent}% discount applied)
                      </span>
                    )}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Next Payment:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(Date.now() + (setupForm.billingFrequency === 'monthly' ? 30 : setupForm.billingFrequency === 'quarterly' ? 90 : 365) * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mt-6">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={setupForm.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1"
                  />
                  <label htmlFor="agreeToTerms" className="ml-3 text-sm text-gray-700">
                    I agree to the automatic payment terms and conditions. I understand that my payment method will be charged automatically according to the billing frequency selected, and I can cancel at any time.
                  </label>
                </div>

                <div className="flex items-start mt-3">
                  <input
                    type="checkbox"
                    id="savePaymentMethod"
                    checked={setupForm.savePaymentMethod}
                    onChange={(e) => handleInputChange('savePaymentMethod', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1"
                  />
                  <label htmlFor="savePaymentMethod" className="ml-3 text-sm text-gray-700">
                    Save payment method for future manual payments
                  </label>
                </div>
              </div>

              {/* Security Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-800">Secure Payment Processing</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Your payment information is encrypted and processed securely by Stripe. We never store your full card details on our servers.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-md font-medium"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!setupForm.agreeToTerms || loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-6 py-2 rounded-md font-medium"
              >
                {loading ? 'Setting up...' : 'Complete Setup'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function for ordinal suffixes
const getOrdinalSuffix = (number) => {
  const j = number % 10;
  const k = number % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

// Helper function to get month name
const getMonthName = (month) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month - 1] || 'January';
};

export default PaymentSystem;